import React, { useEffect, useState } from 'react';
import { apiFetch } from '../../api';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

const s = {
  page: { maxWidth: 900, margin: '32px auto', padding: '0 24px 60px' },
  heading: { fontSize: 24, fontWeight: 700, color: '#0f172a', marginBottom: 6 },
  sub: { fontSize: 14, color: '#64748b', marginBottom: 28 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 16, marginBottom: 32 },
  card: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 22px' },
  cardLabel: { fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 },
  cardValue: { fontSize: 32, fontWeight: 700, color: '#0f172a' },
  cardSub: { fontSize: 12, color: '#64748b', marginTop: 4 },
  section: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 20 },
  sectionTitle: { fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 16 },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  label: { fontSize: 13, color: '#64748b' },
  value: { fontSize: 13, fontWeight: 500, color: '#1e293b' },
  pill: (status) => ({
    fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
    background: status === 'completed' ? '#dcfce7' : status === 'running' ? '#dbeafe' : '#fee2e2',
    color: status === 'completed' ? '#15803d' : status === 'running' ? '#1d4ed8' : '#b91c1c',
  }),
};

export default function AdminOverview() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch('/admin/stats')
      .then((d) => setStats(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div style={s.page}><p style={{ color: '#94a3b8', fontSize: 14 }}>Loading…</p></div>;
  }

  const li = stats?.last_ingestion;

  return (
    <div style={s.page}>
      <h1 style={s.heading}>Admin Overview</h1>
      <p style={s.sub}>System health at a glance</p>

      <div style={s.grid}>
        <div style={s.card}>
          <div style={s.cardLabel}>Total users</div>
          <div style={s.cardValue}>{stats?.total_users ?? '—'}</div>
        </div>
        <div style={s.card}>
          <div style={s.cardLabel}>Tenders in DB</div>
          <div style={s.cardValue}>{stats?.total_tenders ?? '—'}</div>
          <div style={s.cardSub}>{stats?.embedded_tenders} embedded</div>
        </div>
        <div style={s.card}>
          <div style={s.cardLabel}>Pending matches</div>
          <div style={s.cardValue}>{stats?.pending_matches ?? '—'}</div>
          <div style={s.cardSub}>awaiting ranking</div>
        </div>
      </div>

      {/* Last ingestion run */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Last ingestion run</div>
        {li ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={s.row}>
              <span style={s.label}>Status</span>
              <span style={s.pill(li.status)}>{li.status}</span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Started</span>
              <span style={s.value}>{formatDate(li.started_at)}</span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Completed</span>
              <span style={s.value}>{formatDate(li.completed_at)}</span>
            </div>
            <div style={s.row}>
              <span style={s.label}>Tenders fetched / embedded</span>
              <span style={s.value}>{li.tenders_fetched} / {li.tenders_embedded}</span>
            </div>
            {li.error && (
              <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', padding: '8px 12px', borderRadius: 6 }}>
                {li.error}
              </div>
            )}
          </div>
        ) : (
          <p style={{ fontSize: 13, color: '#94a3b8' }}>No ingestion runs yet.</p>
        )}
      </div>

      {/* Match pipeline health */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Match pipeline health</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={s.row}>
            <span style={s.label}>Matches by status</span>
            <span style={s.value}>
              {stats?.matches_by_status
                ? `${stats.matches_by_status.pending} pending · ${stats.matches_by_status.dropped} dropped · ${stats.matches_by_status.failed} failed`
                : '—'}
            </span>
          </div>
          <div style={s.row}>
            <span style={s.label}>Retried (transient errors)</span>
            <span style={s.value}>{stats?.matches_retried ?? '—'}</span>
          </div>
          <div style={s.row}>
            <span style={s.label}>Pending hit attribution</span>
            <span style={s.value}>
              {stats?.pending_main_hit != null
                ? `${stats.pending_main_hit} main-vector · ${stats.pending_synthetic_only} synthetic-only`
                : '—'}
            </span>
          </div>
          <div style={s.row}>
            <span style={s.label}>Verdicts via cosine fallback</span>
            <span style={s.value}>
              {stats?.verdict_cosine_fallbacks ?? '—'}
              {stats?.total_verdicts ? ` / ${stats.total_verdicts}` : ''}
            </span>
          </div>
        </div>
      </div>

    </div>
  );
}
